import { PawPrint, AlertCircle, MapPin, Phone, CheckCircle2 } from "lucide-react";
import shareCardBg from "@/assets/share-card-bg.png";

interface Props {
  backgroundDataUrl?: string;
}

const urgentNeeds = [
  "Karma mokra dla psów",
  "Karma sucha dla psów",
  "Plastikowe baseniki do schładzania psów latem",
];

/**
 * Story 1080x1920: Pilne potrzeby ze schroniska w Opolu - realny przykład z panelu
 */
export const InstagramStoryFirstShelterUrgent = ({ backgroundDataUrl }: Props) => {
  const bgImage = backgroundDataUrl || shareCardBg;

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        fontFamily: "'Nunito', sans-serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#1a3a2a",
      }}
    >
      <img
        src={bgImage}
        alt=""
        draggable={false}
        data-export-bg="true"
        loading="eager"
        decoding="sync"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}
      />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "8px", background: "linear-gradient(90deg, #4E8A6E 0%, #6BAF8D 50%, #4E8A6E 100%)", zIndex: 2 }} />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "100px 72px 60px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Main headline - urgent needs first */}
        <h1 style={{ fontSize: "72px", fontWeight: 900, margin: "0 0 16px", textAlign: "center", lineHeight: 1.08 }}>
          <span style={{ color: "#F59E0B" }}>Pilne potrzeby</span><br />
          <span style={{ color: "#2D5A3D" }}>schroniska w Opolu</span>
        </h1>

        {/* Subtitle - panel usage context */}
        <p style={{ fontSize: "28px", fontWeight: 600, color: "#5A8A70", margin: "0 0 36px", textAlign: "center", lineHeight: 1.35, maxWidth: "900px" }}>
          To jedno z pierwszych schronisk, które zalogowało się do panelu i dodało aktualne potrzeby
        </p>

        {/* Panel label above card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px",
            background: "rgba(78,138,110,0.15)",
            padding: "10px 22px",
            borderRadius: "50px",
            backdropFilter: "blur(4px)",
          }}
        >
          <CheckCircle2 style={{ width: "22px", height: "22px", color: "#4E8A6E", flexShrink: 0 }} strokeWidth={2.5} />
          <span style={{ fontSize: "20px", fontWeight: 800, color: "#4E8A6E", letterSpacing: "0.08em" }}>
            DODANE PRZEZ SCHRONISKO
          </span>
        </div>

        {/* Shelter card - main visual element */}
        <div
          style={{
            width: "100%",
            maxWidth: "920px",
            background: "#FFFFFF",
            borderRadius: "32px",
            boxShadow: "0 24px 60px rgba(15,42,28,0.32), 0 8px 18px rgba(15,42,28,0.18)",
            overflow: "hidden",
            border: "1px solid rgba(45,90,61,0.1)",
          }}
        >
          {/* Shelter header - green */}
          <div
            style={{
              background: "linear-gradient(90deg, #4E8A6E 0%, #5D9A7D 100%)",
              padding: "24px 36px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.25)",
                borderRadius: "50%",
                width: "56px",
                height: "56px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <AlertCircle style={{ width: "32px", height: "32px", color: "#FFFFFF" }} strokeWidth={2.5} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "18px", fontWeight: 800, color: "#FFFFFF", letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.9 }}>
                Pilnie potrzebne
              </span>
              <span style={{ fontSize: "30px", fontWeight: 900, color: "#FFFFFF", lineHeight: 1.1 }}>
                Schronisko dla Bezdomnych<br />Zwierząt w Opolu
              </span>
            </div>
          </div>

          {/* Shelter contact info */}
          <div style={{ padding: "20px 40px 0", display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <MapPin style={{ width: "24px", height: "24px", color: "#5A8A70", flexShrink: 0 }} strokeWidth={2.5} />
              <span style={{ fontSize: "24px", fontWeight: 700, color: "#2D5A3D" }}>ul. Torowa 9, 45-073 Opole</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Phone style={{ width: "24px", height: "24px", color: "#5A8A70", flexShrink: 0 }} strokeWidth={2.5} />
              <span style={{ fontSize: "24px", fontWeight: 700, color: "#2D5A3D" }}>+48 601 490 156</span>
            </div>
          </div>

          {/* Needs list - most prominent */}
          <div style={{ padding: "22px 40px 32px", display: "flex", flexDirection: "column", gap: "14px" }}>
            {urgentNeeds.map((need, i) => (
              <div
                key={need}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "18px",
                  background: "#FFF7ED",
                  padding: "20px 24px",
                  borderRadius: "18px",
                  border: "2px solid #FDBA74",
                }}
              >
                <div
                  style={{
                    background: "#F59E0B",
                    borderRadius: "50%",
                    width: "42px",
                    height: "42px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: "20px", fontWeight: 900, color: "#FFFFFF" }}>{i + 1}</span>
                </div>
                <p style={{ fontSize: "28px", fontWeight: 800, color: "#B45309", margin: 0, lineHeight: 1.25 }}>
                  {need}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          style={{
            marginTop: "36px",
            background: "#4E8A6E",
            padding: "18px 40px",
            borderRadius: "50px",
            boxShadow: "0 8px 24px rgba(78,138,110,0.35)",
          }}
        >
          <span style={{ fontSize: "28px", fontWeight: 800, color: "#FFFFFF" }}>
            Sprawdź profil na gdzieschronisko.pl
          </span>
        </div>
      </div>

      <div style={{ padding: "0 80px 50px", display: "flex", justifyContent: "center", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ background: "#4E8A6E", borderRadius: "12px", width: "44px", height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PawPrint style={{ width: "24px", height: "24px", color: "#FFFFFF" }} />
          </div>
          <span style={{ fontSize: "26px", fontWeight: 700, color: "#2D5A3D" }}>gdzieschronisko.pl</span>
        </div>
      </div>
    </div>
  );
};
